import React, { useState, useEffect, useRef } from 'react';
import { Globe, Check, X, Search, Sparkles } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { oaklandLanguages } from '../../language/languages.js';
import '../../css/components/FullChat/LanguageSelector.css';

const LanguageSelector = ({ isOpen, onClose, onLanguageClick }) => {
  const { currentLanguage, t } = useLanguage();
  const { theme } = useTheme();
  const modalRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Use the same languages as the index page
  const languages = oaklandLanguages;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleLanguageSelect = (languageCode) => {
    onLanguageClick(languageCode);
    onClose();
  };

  const currentLanguageData = languages.find(lang => lang.code === currentLanguage) || languages[0];

  // Filter languages based on search term; exclude current so it only appears in MEVCUT DIL
  const filteredLanguages = languages
    .filter(language =>
      language.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      language.native.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(lang => lang.code !== currentLanguage);

  if (!isOpen) return null;

  return (
    <div className="language-selector-overlay" style={{
      background: 'rgba(0, 0, 0, 0.7)',
      backdropFilter: 'blur(12px)',
      animation: 'fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    }}>
      <div 
        ref={modalRef}
        className="language-selector-popup"
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4), 0 8px 32px rgba(0, 0, 0, 0.3)',
          animation: 'slideInUp 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        {/* Header */}
        <div className="language-selector-header" style={{
          borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
          position: 'relative',
          overflow: 'hidden',
          padding: '24px 28px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '20px'
          }}>
            <div style={{
              width: '56px',
              height: '56px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              borderRadius: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 12px 32px rgba(59, 130, 246, 0.4)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Enhanced glow effect */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.2) 50%, transparent 70%)',
                animation: 'matrixScan 3s linear infinite',
                pointerEvents: 'none'
              }} />
              
              <Globe size={28} style={{ color: 'white', zIndex: 1 }} />
            </div>
            
            <div>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '800',
                color: 'white',
                margin: '0 0 6px 0',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                background: 'linear-gradient(135deg, #3b82f6 0%, #22c55e 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
{t('fullchat.languageSelector')}
              </h2>
              <p style={{
                fontSize: '15px',
                color: 'rgba(255, 255, 255, 0.8)',
                margin: 0,
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                lineHeight: '1.4'
              }}>
{t('fullchat.selectLanguage')}
              </p>
            </div>
          </div>
          
          <button
            onClick={onClose}
            style={{
              width: '40px',
              height: '40px',
              background: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              color: '#ef4444',
              backdropFilter: 'blur(10px)'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(239, 68, 68, 0.25)';
              e.target.style.borderColor = 'rgba(239, 68, 68, 0.5)';
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(239, 68, 68, 0.15)';
              e.target.style.borderColor = 'rgba(239, 68, 68, 0.3)';
              e.target.style.transform = 'scale(1)';
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Current Language Display */}
        <div style={{
          padding: '24px 28px',
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0.03) 100%)',
          borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
          position: 'relative'
        }}>
          <div style={{
            fontSize: '11px',
            fontWeight: '700',
            color: 'rgba(255, 255, 255, 0.6)',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginBottom: '16px',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
          }}>
{t('fullchat.currentLanguage')}
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            padding: '20px 24px',
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0.08) 100%)',
            border: '2px solid rgba(59, 130, 246, 0.4)',
            borderRadius: '20px',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 12px 40px rgba(59, 130, 246, 0.3)'
          }}>
            {/* Enhanced glow effect */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%)',
              animation: 'matrixScan 3s linear infinite',
              pointerEvents: 'none'
            }} />
            
            <div style={{
              fontSize: '36px',
              filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))',
              animation: 'float 3s ease-in-out infinite'
            }}>
              {currentLanguageData.flag}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '18px',
                fontWeight: '700',
                color: 'white',
                marginBottom: '6px',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
              }}>
                {currentLanguageData.native}
              </div>
              <div style={{
                fontSize: '15px',
                color: 'rgba(255, 255, 255, 0.8)',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
              }}>
                {currentLanguageData.name}
              </div>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              background: 'rgba(34, 197, 94, 0.25)',
              borderRadius: '10px',
              color: '#22c55e',
              border: '1px solid rgba(34, 197, 94, 0.4)',
              backdropFilter: 'blur(10px)'
            }}>
              <Check size={18} />
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div style={{
          padding: '24px 28px 20px 28px',
          background: 'rgba(15, 23, 42, 0.6)'
        }}>
          <div style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Search 
              size={20} 
              style={{ 
                position: 'absolute', 
                left: '16px', 
                color: 'rgba(255, 255, 255, 0.5)',
                zIndex: 1
              }} 
            />
            <input
              type="text"
              placeholder={t('fullchat.searchLanguages')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '16px 16px 16px 48px',
                background: 'rgba(30, 41, 59, 0.8)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '12px',
                color: 'white',
                fontSize: '15px',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                outline: 'none',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(10px)'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'rgba(59, 130, 246, 0.5)';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(59, 130, 246, 0.2)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>

        {/* Language List */}
        <div style={{
          maxHeight: '400px',
          overflowY: 'auto',
          padding: '20px 28px 120px 28px',
          background: 'rgba(15, 23, 42, 0.4)'
        }}>
          <h4 style={{
            fontSize: '11px',
            fontWeight: '700',
            color: 'rgba(255, 255, 255, 0.6)',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            margin: '0 0 20px 0',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
          }}>
{t('fullchat.availableLanguages')} ({filteredLanguages.length})
          </h4>
          
          <div style={{
            display: 'grid',
            gap: '12px'
          }}>
            {filteredLanguages.map((language, index) => (
              <button
                key={language.code}
                onClick={() => handleLanguageSelect(language.code)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '16px 20px',
                  background: currentLanguage === language.code 
                    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(59, 130, 246, 0.1) 100%)'
                    : 'rgba(30, 41, 59, 0.6)',
                  border: currentLanguage === language.code 
                    ? '2px solid rgba(59, 130, 246, 0.5)'
                    : '1px solid rgba(59, 130, 246, 0.1)',
                  borderRadius: '16px',
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  textAlign: 'left',
                  width: '100%',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: currentLanguage === language.code 
                    ? '0 8px 32px rgba(59, 130, 246, 0.3)'
                    : '0 2px 8px rgba(0, 0, 0, 0.1)'
                }}
                onMouseEnter={(e) => {
                  if (currentLanguage !== language.code) {
                    e.target.style.background = 'rgba(30, 41, 59, 0.8)';
                    e.target.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentLanguage !== language.code) {
                    e.target.style.background = 'rgba(30, 41, 59, 0.6)';
                    e.target.style.borderColor = 'rgba(59, 130, 246, 0.1)';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                  }
                }}
              >
                {/* Enhanced glow effect for selected language */}
                {currentLanguage === language.code && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(45deg, transparent 30%, rgba(59, 130, 246, 0.1) 50%, transparent 70%)',
                    animation: 'matrixScan 3s linear infinite',
                    pointerEvents: 'none'
                  }} />
                )}
                
                <div style={{
                  fontSize: '24px',
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
                  zIndex: 1
                }}>
                  {language.flag}
                </div>
                
                <div style={{ flex: 1, zIndex: 1 }}>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: 'white',
                    marginBottom: '4px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                  }}>
                    {language.native}
                  </div>
                  <div style={{
                    fontSize: '14px',
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                  }}>
                    {language.name}
                  </div>
                </div>
                
                {currentLanguage === language.code && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    background: 'rgba(34, 197, 94, 0.25)',
                    borderRadius: '8px',
                    color: '#22c55e',
                    border: '1px solid rgba(34, 197, 94, 0.4)',
                    backdropFilter: 'blur(10px)',
                    zIndex: 1
                  }}>
                    <Check size={16} />
                  </div>
                )}
              </button>
            ))}
          </div>
          
          {filteredLanguages.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: 'rgba(255, 255, 255, 0.6)',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            }}>
              <div style={{ fontSize: '64px', marginBottom: '20px', opacity: 0.6 }}>🔍</div>
              <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: 'white' }}>
{t('fullchat.noLanguagesFound')}
              </div>
              <div style={{ fontSize: '15px', opacity: 0.7 }}>
                Try adjusting your search terms
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '24px 28px',
          borderTop: '1px solid rgba(59, 130, 246, 0.2)',
          background: 'rgba(15, 23, 42, 0.6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{
            fontSize: '13px',
            color: 'rgba(255, 255, 255, 0.6)',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Sparkles size={14} />
{languages.length} {t('fullchat.languagesAvailable')}
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '12px 24px',
              background: 'rgba(30, 41, 59, 0.8)',
              border: '2px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '12px',
              color: 'rgba(255, 255, 255, 0.8)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              fontSize: '14px',
              fontWeight: '600'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(30, 41, 59, 0.9)';
              e.target.style.color = 'white';
              e.target.style.borderColor = 'rgba(59, 130, 246, 0.5)';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(30, 41, 59, 0.8)';
              e.target.style.color = 'rgba(255, 255, 255, 0.8)';
              e.target.style.borderColor = 'rgba(59, 130, 246, 0.3)';
              e.target.style.transform = 'translateY(0)';
            }}
          >
{t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LanguageSelector; 